import { hasValue } from "@repo/helpers";
import { useQuery } from "@tanstack/react-query";
import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useState } from "react";

import type { User } from "../models";

import { AccountController } from "../api/accountController";
import { GET_USER_INFO } from "../api/queryKeys";
import { UserInfoApi } from "../api/userInfoApi";

interface UserContextType {
	user?: User;
	loggedIn?: boolean;
	updateUser: (user?: Partial<User>) => void;
	onAuthError: (err?: string) => void;
	logout: () => Promise<void>;
	loading: boolean;
}

export const UserContext = createContext<UserContextType>({
	user: undefined,
	updateUser: () => undefined,
	onAuthError: () => undefined,
	logout: () => undefined,
	loggedIn: false,
	loading: false,
});

interface UserProviderProps {
	children: ReactNode;
}

export function useUserContext() {
	return useContext(UserContext);
}

export const UserProvider: FC<UserProviderProps> = ({ children }) => {
	const [user, setUser] = useState<User>(getStoredUser);
	const loggedIn = user.loggedIn;
	// The query settles one render before `user` catches up; until then `user` is the stored one.
	const [userInfoApplied, setUserInfoApplied] = useState(false);

	const userInfoQuery = useQuery({
		queryKey: [GET_USER_INFO],
		queryFn: () => UserInfoApi.getUserInfo(),
	});

	useEffect(() => {
		setStoredUser(user);
	}, [user]);

	// Another provider on the page, or another tab, clears the stored user when it logs out.
	useEffect(() => {
		const timer = setInterval(() => {
			if (!isUserStored()) setUser({} as User);
		}, 25);
		return () => clearInterval(timer);
	}, []);

	useEffect(() => {
		if (userInfoQuery.isLoading) return;
		// oxlint-disable-next-line react/set-state-in-effect -- copies the fetched user into state that updateUser and onAuthError also change
		setUserInfoApplied(true);
		if (!hasValue(userInfoQuery.data)) {
			setUser(defaultUser);
			return;
		}

		setUser({ ...userInfoQuery.data, loggedIn: true });
	}, [userInfoQuery.isLoading, userInfoQuery.data]);

	const onAuthError = useCallback(
		function (loginError?: string) {
			clearStoredUser();
			setUser({ loginError } as User);
		},
		[setUser],
	);

	function updateUser(newUser: User) {
		setUser({ ...user, ...newUser });
	}

	async function logout() {
		clearStoredUser();
		setUser(defaultUser);
		await AccountController.logout();
	}

	return (
		<UserContext.Provider value={{ user, updateUser, onAuthError, logout, loggedIn, loading: !userInfoApplied }}>
			{children}
		</UserContext.Provider>
	);
};

const userNameStorage = `TLPCloud_LastLoggedInUsername`;
const userStorage = `TLPCloud_User`;
function setStoredUser(user: User) {
	localStorage.setItem(userStorage, JSON.stringify(user));
}
export function clearStoredUser() {
	localStorage.removeItem(userStorage);
}

function isUserStored() {
	return !!localStorage.getItem(userStorage);
}
function getStoredUser() {
	const json = localStorage.getItem(userStorage);
	const storedValue = json && (JSON.parse(json) as User);
	return storedValue ?? defaultUser;
}
export function storeUserName(username: string) {
	localStorage.setItem(userNameStorage, username);
}
export function clearUserName() {
	localStorage.removeItem(userNameStorage);
}
export function getStoredUserName() {
	return localStorage.getItem(userNameStorage);
}

const defaultUser = {
	userId: null,
	userName: null,
	userIsAdmin: false,
	userIsVendorUser: false,
	userIsVendorAdmin: false,
	loggedIn: false,
	loginError: null,
	loading: true,
} as User;
